// Kinio Node 的〔終端機〕（SPEC #node-ttl-vms-terminal、#node-ttl-relay、KN-T26 / T29）：經平台 console 中繼讀寫節點 UART1 接的設備。
// 票：POST /v1/homes/{home}/things/{device}/console {mode} → {ws_url}；WebSocket 幀照 SPEC #node-s11-8-1（呼叫者端 WebSocket）。
// 這裡只放不碰 DOM 的部分（幀 → 畫面狀態），元件在 NodeTerminal.tsx。

/** Kinio Node 本體（kinio-node-*，不含 Node Host）才有〔終端機〕：Host 的 console 要帶 channel，只走設備群控制。 */
export function isKinioNode(model: string | undefined): boolean {
  return !!model && model.startsWith('kinio-node-') && model !== 'kinio-node-host';
}

export type ConsoleStatus = 'idle' | 'ticket' | 'connecting' | 'opening' | 'open' | 'reconnecting' | 'closed';

export interface ConsoleView {
  status: ConsoleStatus;
  /** closed / reconnecting 的原因（device_offline、data_lost…）或要票失敗的錯誤碼 */
  reason?: string;
  /** 這條連線的 client_id（hello） */
  me: string;
  /** 目前的寫入者 client_id；空 = 沒人在寫 */
  writer: string;
  /** 票的模式：write 才拿得到寫入權 */
  mode: 'read' | 'write';
  readers: number;
  /** 收到的輸出（已解碼、去掉 ANSI 控制序列），超過上限只留尾段 */
  out: string;
  /** 中繼記帳：佇列滿丟掉的幀、設備 TX 放不下的輸入位元組、斷線續傳接不上丟掉的位元組 */
  droppedFrames: number;
  inDropped: number;
  lost: number;
  deviceRebooted: boolean;
  /** 最近一個錯誤幀的 code（NOT_WRITER、RATE_LIMITED、DEVICE_RECONNECTING…） */
  lastError?: string;
}

export const MAX_OUT = 200_000;
const KEEP_OUT = 150_000;

export function initialView(mode: 'read' | 'write' = 'write'): ConsoleView {
  return { status: 'idle', me: '', writer: '', mode, readers: 0, out: '', droppedFrames: 0, inDropped: 0, lost: 0, deviceRebooted: false };
}

/** base64 → UTF-8 字串（壞的位元組照 TextDecoder 換成 U+FFFD，不丟）。 */
export function decodeData(b64: string): string {
  try {
    const bin = atob(b64);
    const u = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i);
    return new TextDecoder().decode(u);
  } catch {
    return '';
  }
}

/** base64 → 原始位元組（終端機照原樣顯示，不去控制序列）；壞的 base64 回空。 */
export function decodeBytes(b64: string): Uint8Array {
  try {
    const bin = atob(b64);
    const u = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i);
    return u;
  } catch {
    return new Uint8Array(0);
  }
}

/** 字串 → UTF-8 → base64（input 幀）。 */
export function encodeData(s: string): string {
  const u = new TextEncoder().encode(s);
  let b = '';
  u.forEach((x) => (b += String.fromCharCode(x)));
  return btoa(b);
}

/** 去掉 ANSI / VT100 控制序列（顏色、游標移動）、\r 與其他控制字元（\n、\t 留著）。 */
export function cleanText(s: string): string {
  return s
    .replace(/\x1b\[[0-9;?]*[ -/]*[@-~]/g, '') // CSI
    .replace(/\x1b\][^\x07\x1b]*(\x07|\x1b\\)/g, '') // OSC
    .replace(/\x1b[@-Z\\-_]/g, '') // 其他 ESC 單字元
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '')
    .replace(/[\x00-\x08\x0b-\x1f\x7f]/g, '');
}

function append(out: string, s: string): string {
  const n = out + s;
  return n.length > MAX_OUT ? n.slice(-KEEP_OUT) : n;
}

/** 伺服器幀 → 新的畫面狀態。認不得的幀原樣回（前向相容）。
 *  opts.skipOut：data 幀只記帳、不累積 out（終端機自己把原始位元組寫進 xterm）。 */
export function applyFrame(v: ConsoleView, raw: string, opts?: { skipOut?: boolean }): ConsoleView {
  let m: Record<string, unknown>;
  try {
    m = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return v;
  }
  const num = (x: unknown) => (typeof x === 'number' && x > 0 ? x : 0);
  switch (m.type) {
    case 'hello':
      return {
        ...v,
        status: v.status === 'connecting' ? 'opening' : v.status,
        me: String(m.client_id ?? ''),
        writer: String(m.writer ?? ''),
        readers: num(m.readers),
      };
    case 'state': {
      const st = String(m.state ?? '');
      const status: ConsoleStatus = st === 'open' || st === 'opening' || st === 'reconnecting' || st === 'closed' ? st : v.status;
      return {
        ...v,
        status,
        reason: typeof m.reason === 'string' ? m.reason : status === 'open' ? undefined : v.reason,
        lost: v.lost + num(m.lost),
        deviceRebooted: v.deviceRebooted || m.device_rebooted === true,
      };
    }
    case 'role':
      return { ...v, writer: String(m.writer ?? '') };
    case 'data':
      return {
        ...v,
        out: opts?.skipOut ? v.out : append(v.out, cleanText(decodeData(String(m.data ?? '')))),
        droppedFrames: v.droppedFrames + num(m.dropped),
        inDropped: v.inDropped + num(m.in_dropped),
      };
    case 'error':
      return { ...v, lastError: String(m.code ?? 'ERROR') };
    default:
      return v;
  }
}

/** 我是不是寫入者（只有 write 票、hello 之後、writer = 自己）。 */
export function amWriter(v: ConsoleView): boolean {
  return v.mode === 'write' && v.me !== '' && v.writer === v.me;
}

/** 〔在自己的終端機開〕的指令（SPEC #node-ttl-vms-terminal）：nodectl 在本機當 tunnel、自動選路；不放 IP、token、金鑰。 */
export function tunnelCommand(aid: string, homeId: string): string {
  return `nodectl ttl ${aid} --home ${homeId}`;
}

/** 新視窗的網址（只有終端機、沒有側欄）。 */
export function terminalHref(homeId: string, aid: string): string {
  return `/terminal/${encodeURIComponent(homeId)}/${encodeURIComponent(aid)}`;
}

/** 連上時另外有幾條連線（hello 的 readers 含自己；R3-P0-4「有人連線中要說」）。還沒連上回 0（不猜）。 */
export function othersConnected(v: Pick<ConsoleView, 'me' | 'readers'>): number {
  return v.me ? Math.max(0, v.readers - 1) : 0;
}
